/**
 * Date-only formatting utilities
 * 
 * These functions handle date-only values (YYYY-MM-DD) without timezone conversion.
 * IMPORTANT: Never convert date-only strings to Date objects - it causes timezone shifts.
 * 
 * Use cases:
 * - formatDateOnly: Display date-only strings as MM/DD/YYYY (for UI display)
 * - normalizeDateForInput: Normalize dates to YYYY-MM-DD format (for HTML date inputs)
 * - extractDateOnly: Extract YYYY-MM-DD from any date format (for processing)
 * - formatDateForBackend: Ensure dates are in YYYY-MM-DD before sending to backend
 */

/**
 * Format date-only string (YYYY-MM-DD) for display as MM/DD/YYYY
 * This prevents timezone shifts when displaying dates
 * 
 * @param {string} dateString - Date string in YYYY-MM-DD format or ISO string
 * @returns {string} Formatted date as MM/DD/YYYY or "N/A" if invalid
 */
export const formatDateOnly = (dateString) => {
  if (!dateString) return "N/A";
  
  // If it's already in YYYY-MM-DD format, format it directly
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-');
    // Format as MM/DD/YYYY (US format)
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  }
  
  // If it's an ISO datetime string, extract just the date part
  const isoMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) {
    const [year, month, day] = isoMatch[1].split('-');
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  }
  
  // Fallback: return as-is if not in expected format
  return dateString;
};

/**
 * Normalize date to YYYY-MM-DD format for HTML date inputs
 * This prevents timezone shifts when setting date input values
 * 
 * @param {string} dateString - Date string in various formats
 * @returns {string} Normalized date in YYYY-MM-DD format or empty string
 */
export const normalizeDateForInput = (dateString) => {
  if (!dateString) return "";
  
  // If already in YYYY-MM-DD format, return as is (most common case)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  
  // Try to extract YYYY-MM-DD directly from ISO string (e.g., "2024-01-15T00:00:00.000Z" -> "2024-01-15")
  // This avoids creating a Date object which would cause timezone conversion
  const isoMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) {
    return isoMatch[1];
  }
  
  // Try to extract from other common formats without using Date object
  // Handle formats like "2024/01/15"
  const slashMatch = dateString.match(/^(\d{4})\/(\d{2})\/(\d{2})/);
  if (slashMatch) {
    return `${slashMatch[1]}-${slashMatch[2]}-${slashMatch[3]}`;
  }
  
  // Last resort: Only use Date object if absolutely necessary, and use UTC methods
  // This should rarely be needed if backend always sends YYYY-MM-DD format
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      // Use UTC methods to avoid timezone conversion
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
  } catch (e) {
    console.warn("Error parsing date:", dateString, e);
  }
  
  return "";
};

/**
 * Extract date-only value (YYYY-MM-DD) from any date format
 * This ensures we always work with YYYY-MM-DD format without timezone conversion
 * 
 * @param {string|Date} dateValue - Date value in various formats
 * @returns {string} Extracted date in YYYY-MM-DD format or empty string
 */
export const extractDateOnly = (dateValue) => {
  if (!dateValue) return "";
  
  // If it's already a clean YYYY-MM-DD string, return it (most common case)
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }
  
  // If it's a string, try to extract YYYY-MM-DD using regex (no Date object needed)
  if (typeof dateValue === 'string') {
    // Extract from ISO string: "2024-01-15T00:00:00.000Z" -> "2024-01-15"
    const isoMatch = dateValue.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) {
      return isoMatch[1];
    }
    
    // Handle other string formats if needed
    const slashMatch = dateValue.match(/^(\d{4})\/(\d{2})\/(\d{2})/);
    if (slashMatch) {
      return `${slashMatch[1]}-${slashMatch[2]}-${slashMatch[3]}`;
    }
  }
  
  // Last resort: Only if dateValue is a Date object, use UTC methods
  // This should rarely be needed if backend always sends strings
  if (dateValue instanceof Date) {
    try {
      if (!isNaN(dateValue.getTime())) {
        // Use UTC methods to avoid timezone conversion
        const year = dateValue.getUTCFullYear();
        const month = String(dateValue.getUTCMonth() + 1).padStart(2, "0");
        const day = String(dateValue.getUTCDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      console.warn("Error parsing Date object:", dateValue, e);
    }
  }
  
  return "";
};

/**
 * Ensure date is in YYYY-MM-DD format before sending to backend
 * This prevents timezone conversion issues on the backend
 * 
 * @param {string} dateString - Date string (should already be in YYYY-MM-DD from HTML inputs)
 * @returns {string} Date in YYYY-MM-DD format or empty string
 */
export const formatDateForBackend = (dateString) => {
  if (!dateString) return "";
  
  // If already in YYYY-MM-DD format, return as is (this is what date inputs provide)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  
  // For date inputs, they should already be in YYYY-MM-DD format
  // But if somehow we get a Date object or other format, normalize it
  return normalizeDateForInput(dateString);
};

